import type {
  ConnectActivityConfig,
  ConnectActivityLevelResult,
  ConnectActivityMentorAnswer,
  ConnectActivityResult,
  ConnectActivityTeenResponse,
} from "@/lib/api/activities";

export interface ConnectTeenVoiceAuthorAnswer {
  question_id: string;
  mode: "voice";
  audio_s3_key: string;
  duration_ms: number;
  transcript?: string | null;
}

export interface ConnectTeenTextAuthorAnswer {
  question_id: string;
  mode: "text";
  content: string;
}

export type ConnectTeenAuthorAnswer =
  | ConnectTeenVoiceAuthorAnswer
  | ConnectTeenTextAuthorAnswer;

export interface ConnectTeenActivityConfig {
  cardset_id?: string;
  question_ids: string[];
  author_answers: ConnectTeenAuthorAnswer[] | null;
}

export interface ConnectResponderAnswer {
  question_id: string;
  audio_s3_key: string;
  duration_ms: number;
  transcript?: string | null;
}

export interface ConnectResponderResult {
  responder_answers: ConnectResponderAnswer[];
}

export const countConnectLevelsCompleted = (result: unknown): number => {
  if (!result || typeof result !== "object") return 0;
  const shaped = result as Partial<ConnectActivityResult> &
    Partial<ConnectResponderResult>;
  if (Array.isArray(shaped.levels)) {
    return shaped.levels.filter((level) => level !== null).length;
  }
  if (Array.isArray(shaped.responder_answers)) {
    return shaped.responder_answers.length;
  }
  return 0;
};

export const isTeenInitiatedConnectConfig = (
  config: unknown,
): config is ConnectTeenActivityConfig =>
  !!config &&
  typeof config === "object" &&
  "question_ids" in (config as Record<string, unknown>);

const toTeenResponse = (
  answer: ConnectTeenAuthorAnswer | undefined,
): ConnectActivityTeenResponse | null => {
  if (!answer) return null;
  if (answer.mode === "text") {
    return { mode: "text", content: answer.content };
  }
  return {
    mode: "voice",
    audio_s3_key: answer.audio_s3_key,
    duration_ms: answer.duration_ms,
    transcript: answer.transcript ?? null,
  };
};

// Teen-initiated Connects invert who's in the config vs the result; map both
// shapes to the mentor_answers/levels layout the renderers expect.
export const normalizeConnectShapes = (
  config: unknown,
  result: unknown,
  completedAt: string | null,
): { config: ConnectActivityConfig; result: ConnectActivityResult | null } => {
  if (!isTeenInitiatedConnectConfig(config)) {
    return {
      config: config as ConnectActivityConfig,
      result: (result as ConnectActivityResult | null) ?? null,
    };
  }

  const responderAnswers =
    (result as ConnectResponderResult | null)?.responder_answers ?? [];

  const mentorAnswers = config.question_ids.map((questionId, i) => {
    const responder = responderAnswers[i];
    return {
      question_id: questionId,
      library_answer_id: "",
      audio_s3_key: responder?.audio_s3_key ?? null,
      duration_ms: responder?.duration_ms ?? null,
      transcript: responder?.transcript ?? null,
    } satisfies ConnectActivityMentorAnswer;
  }) as ConnectActivityConfig["mentor_answers"];

  const levels = config.question_ids.map((_, i) => {
    const teenResponse = completedAt
      ? toTeenResponse(config.author_answers?.[i])
      : null;
    if (!teenResponse) return null;
    return {
      teen_response: teenResponse,
      revealed_at: completedAt,
      advanced_at: completedAt,
    } satisfies ConnectActivityLevelResult;
  }) as ConnectActivityResult["levels"];

  return {
    config: {
      cardset_id: config.cardset_id ?? "default_v1",
      mentor_answers: mentorAnswers,
    },
    result: { levels },
  };
};
